import { Check, MessageCircle, PackageCheck, Sparkles } from "lucide-react";

import { CatalogoCard } from "@/components/site/CatalogoCard";
import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { track } from "@/lib/analytics";
import { WHATSAPP } from "@/lib/contacto";
import { productos, type CategoriaProducto } from "@/lib/productos";

export type PreguntaSeo = {
  pregunta: string;
  respuesta: string;
};

type CasoUso = {
  titulo: string;
  texto: string;
};

export type CategoriaSeoConfig = {
  path: string;
  breadcrumb: string;
  eyebrow: string;
  title: string;
  intro: string;
  category: CategoriaProducto;
  featuredId: string;
  benefits: string[];
  sectionLabel: string;
  sectionTitle: string;
  sectionCopy: string;
  useCases: CasoUso[];
  faqs: PreguntaSeo[];
  finalTitle: string;
  finalCopy: string;
  whatsappText: string;
};

export function CategoriaSeoPage({ config }: { config: CategoriaSeoConfig }) {
  const modelos = productos.filter((producto) => producto.category === config.category);
  const whatsappHref = `${WHATSAPP.split("?")[0]}?text=${encodeURIComponent(config.whatsappText)}`;

  if (modelos.length === 0) return null;

  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <section className="pv-page-hero overflow-hidden border-b border-ink/10">
          <div className="mx-auto max-w-[1320px] px-5 py-7 sm:px-8 sm:py-12 lg:px-10">
            <nav aria-label="Ruta de navegación" className="text-sm text-slate">
              <a href="/" className="hover:text-ink hover:underline">
                Inicio
              </a>
              <span aria-hidden="true"> / </span>
              <span>{config.breadcrumb}</span>
            </nav>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-primary sm:mt-7">
              {config.eyebrow}
            </p>
            <h1 className="mt-2 max-w-4xl font-display text-4xl font-extrabold uppercase leading-[0.92] text-ink sm:mt-3 sm:text-6xl lg:text-7xl">
              {config.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate sm:mt-5 sm:text-lg">
              {config.intro}
            </p>
          </div>
        </section>

        <section
          id="modelos"
          className="pv-catalog-section scroll-mt-28"
          aria-label="Modelos disponibles"
        >
          <div className="mx-auto max-w-[1320px] px-5 pb-8 pt-5 sm:px-8 sm:pb-12 sm:pt-8 lg:px-10">
            <div
              className={`grid grid-cols-2 gap-3 sm:gap-5 ${
                modelos.length > 2
                  ? "lg:grid-cols-3 xl:grid-cols-4"
                  : modelos.length === 1
                    ? "max-w-[620px]"
                    : "lg:max-w-[620px]"
              }`}
            >
              {modelos.map((producto) => (
                <CatalogoCard key={producto.id} producto={producto} headingLevel={2} />
              ))}
            </div>
          </div>
        </section>

        <BeneficiosCategoria config={config} />

        <section className="pv-dark-section text-white">
          <div className="mx-auto grid max-w-[1320px] gap-6 px-5 py-8 sm:gap-10 sm:px-8 sm:py-14 lg:grid-cols-[.9fr_1.1fr] lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-signal">
                {config.sectionLabel}
              </p>
              <h2 className="mt-2 font-display text-3xl font-extrabold uppercase leading-none sm:mt-3 sm:text-5xl">
                {config.sectionTitle}
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:mt-5 sm:text-base">
                {config.sectionCopy}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {config.useCases.map(({ titulo, texto }) => (
                <article
                  key={titulo}
                  className="pv-dark-card rounded-2xl p-4 sm:rounded-3xl sm:p-5"
                >
                  <h3 className="font-bold">{titulo}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{texto}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="pv-faq-section" aria-labelledby="preguntas-categoria-seo">
          <div className="mx-auto grid max-w-[1100px] gap-5 px-5 py-8 sm:gap-8 sm:px-8 sm:py-14 lg:grid-cols-[.75fr_1.25fr] lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                Preguntas
              </p>
              <h2
                id="preguntas-categoria-seo"
                className="mt-2 font-display text-3xl font-extrabold uppercase leading-none text-ink sm:mt-3 sm:text-5xl"
              >
                Lo que necesitas saber.
              </h2>
            </div>
            <div className="divide-y divide-ink/10 border-y border-ink/10">
              {config.faqs.map(({ pregunta, respuesta }) => (
                <details key={pregunta} className="pv-accordion-row group py-1">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-5 py-4 text-sm font-bold text-ink sm:py-5 sm:text-base [&::-webkit-details-marker]:hidden">
                    {pregunta}
                    <span
                      className="text-xl text-primary transition-transform group-open:rotate-45"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </summary>
                  <p className="max-w-2xl pb-5 text-sm leading-relaxed text-slate">{respuesta}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="pv-final-cta">
          <div className="mx-auto flex max-w-[1100px] flex-col items-center px-5 py-8 text-center sm:px-8 sm:py-12">
            <h2 className="font-display text-3xl font-extrabold uppercase leading-none text-ink sm:text-5xl">
              {config.finalTitle}
            </h2>
            <p className="mt-4 max-w-2xl leading-relaxed text-slate">{config.finalCopy}</p>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { ubicacion: `${config.path}_final` })}
              className="pv-button pv-button--primary mt-7 inline-flex min-h-12 items-center justify-center gap-2 px-7 py-3.5"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Comprar por WhatsApp
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function BeneficiosCategoria({
  config,
  className,
}: {
  config: CategoriaSeoConfig;
  className?: string;
}) {
  return (
    <section className={`pv-benefit-band ${className ?? ""}`}>
      <div className="pv-benefit-rail mx-auto grid max-w-[1240px] grid-cols-3 gap-0 px-3 sm:px-8 lg:px-10">
        {config.benefits.slice(0, 3).map((beneficio, index) => {
          const Icon = index === 0 ? Sparkles : index === 1 ? PackageCheck : Check;
          return (
            <article
              key={beneficio}
              className="border-r border-ink/10 px-2 py-4 text-center last:border-r-0 sm:px-5 sm:py-7 sm:text-left"
            >
              <Icon
                className="mx-auto h-5 w-5 text-primary sm:mx-0 sm:h-6 sm:w-6"
                aria-hidden="true"
              />
              <h2 className="mt-2 text-[0.7rem] font-bold leading-tight text-ink sm:mt-3 sm:text-lg">
                {beneficio}
              </h2>
            </article>
          );
        })}
      </div>
    </section>
  );
}
